import React, { useMemo, useState } from "react";
import { useCustom, useList } from "@refinedev/core";
import { Card, Col, DatePicker, Empty, Row, Select, Statistic, Table, Tabs, Typography, Button, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import dayjs from "dayjs";
import { DownloadOutlined } from "@ant-design/icons";
import { downloadCsvReport, downloadPdfReport } from "../../utils/reportExport";

const { RangePicker } = DatePicker as any;
const { Title } = Typography;

type DailySalesRow = {
    date: string;
    currencyCode: string;
    saleCount: number;
    totalSalesAmount: number;
};

type ProductPerformanceRow = {
    inventoryItemId: number;
    itemCode: string;
    itemName: string;
    quantitySold: number;
    totalSalesAmount: number;
};

type CashierPerformanceRow = {
    cashierUserId: string;
    cashierName: string | null;
    saleCount: number;
    totalSalesAmount: number;
};

export const SalesReport: React.FC = () => {
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().startOf("day"),
        dayjs().endOf("day"),
    ]);
    const [activeReportTab, setActiveReportTab] = useState<string>("daily");
    const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>(undefined);

    const fromDate = dateRange[0].toISOString();
    const toDate = dateRange[1].toISOString();

    const { data: currenciesData } = useList({ resource: "currency", dataProviderName: "default" });
    const currencyOptions =
        currenciesData?.data?.map((c: any) => ({
            value: c.currencyCode,
            label: `${c.currencyCode} (${c.name ?? c.currencyCode})`,
        })) ?? [];

    const { data: dailySalesResp, isLoading: dailySalesLoading } = useCustom({
        url: "operational-reports/daily-sales",
        method: "get",
        config: { query: { fromDate, toDate } },
    });

    const { data: productResp, isLoading: productLoading } = useCustom({
        url: "operational-reports/product-performance",
        method: "get",
        config: { query: { fromDate, toDate } },
    });

    const { data: cashierResp, isLoading: cashierLoading } = useCustom({
        url: "operational-reports/cashier-performance",
        method: "get",
        config: { query: { fromDate, toDate } },
    });

    const dailyRows: DailySalesRow[] = useMemo(() => {
        const outer = dailySalesResp as { data?: { data?: { rows?: DailySalesRow[] }; rows?: DailySalesRow[] } } | undefined;
        const report = outer?.data?.data ?? outer?.data;
        const rows = (report?.rows ?? []) as DailySalesRow[];
        return selectedCurrency ? rows.filter((r) => r.currencyCode === selectedCurrency) : rows;
    }, [dailySalesResp, selectedCurrency]);

    const productRows: ProductPerformanceRow[] = useMemo(() => {
        const outer = productResp as { data?: { data?: { rows?: ProductPerformanceRow[] }; rows?: ProductPerformanceRow[] } } | undefined;
        const report = outer?.data?.data ?? outer?.data;
        return (report?.rows ?? []) as ProductPerformanceRow[];
    }, [productResp]);

    const cashierRows: CashierPerformanceRow[] = useMemo(() => {
        const outer = cashierResp as { data?: { data?: { rows?: CashierPerformanceRow[] }; rows?: CashierPerformanceRow[] } } | undefined;
        const report = outer?.data?.data ?? outer?.data;
        return (report?.rows ?? []) as CashierPerformanceRow[];
    }, [cashierResp]);

    const totalSales = useMemo(
        () => dailyRows.reduce((sum, r) => sum + (Number(r.totalSalesAmount) || 0), 0),
        [dailyRows]
    );
    const totalCount = useMemo(() => dailyRows.reduce((sum, r) => sum + (Number(r.saleCount) || 0), 0), [dailyRows]);

    const chartData = useMemo(
        () =>
            dailyRows.map((r) => ({
                name: r.date,
                total: r.totalSalesAmount,
            })),
        [dailyRows]
    );

    const dailyColumns: ColumnsType<DailySalesRow> = [
        { title: "Date", dataIndex: "date", key: "date" },
        { title: "Currency", dataIndex: "currencyCode", key: "currencyCode" },
        { title: "Transactions", dataIndex: "saleCount", key: "saleCount" },
        {
            title: "Total Sales",
            dataIndex: "totalSalesAmount",
            key: "totalSalesAmount",
            render: (v) => Number(v).toFixed(2),
        },
    ];

    const productColumns: ColumnsType<ProductPerformanceRow> = [
        { title: "Item", dataIndex: "itemName", key: "itemName" },
        { title: "Code", dataIndex: "itemCode", key: "itemCode" },
        { title: "Qty Sold", dataIndex: "quantitySold", key: "quantitySold" },
        {
            title: "Total Sales",
            dataIndex: "totalSalesAmount",
            key: "totalSalesAmount",
            render: (v) => Number(v).toFixed(2),
        },
    ];

    const cashierColumns: ColumnsType<CashierPerformanceRow> = [
        {
            title: "Cashier",
            dataIndex: "cashierName",
            key: "cashierName",
            render: (v, r) => v ?? r.cashierUserId,
        },
        { title: "Transactions", dataIndex: "saleCount", key: "saleCount" },
        {
            title: "Total Sales",
            dataIndex: "totalSalesAmount",
            key: "totalSalesAmount",
            render: (v) => Number(v).toFixed(2),
        },
    ];

    const salesExportSubtitle = `${dateRange[0].format("YYYY-MM-DD")} – ${dateRange[1].format("YYYY-MM-DD")}${
        selectedCurrency ? ` · ${selectedCurrency}` : " · All currencies"
    }`;

    const handleSalesExportExcel = () => {
        if (activeReportTab === "daily") {
            const headers = ["Date", "Currency", "Transactions", "Total Sales"];
            const rows = dailyRows.map((r) => [r.date, r.currencyCode, r.saleCount, Number(r.totalSalesAmount).toFixed(2)]);
            downloadCsvReport("sales-daily", headers, rows);
        } else if (activeReportTab === "products") {
            const headers = ["Item", "Code", "Qty Sold", "Total Sales"];
            const rows = productRows.map((r) => [r.itemName, r.itemCode, r.quantitySold, Number(r.totalSalesAmount).toFixed(2)]);
            downloadCsvReport("sales-product-performance", headers, rows);
        } else {
            const headers = ["Cashier", "Transactions", "Total Sales"];
            const rows = cashierRows.map((r) => [r.cashierName ?? r.cashierUserId, r.saleCount, Number(r.totalSalesAmount).toFixed(2)]);
            downloadCsvReport("sales-cashier-performance", headers, rows);
        }
        message.success("Export downloaded (CSV opens in Excel).");
    };

    const handleSalesExportPdf = async () => {
        try {
            if (activeReportTab === "daily") {
                const headers = ["Date", "Cur.", "Txns", "Total"];
                const body = dailyRows.map((r) => [r.date, r.currencyCode, r.saleCount, Number(r.totalSalesAmount).toFixed(2)]);
                await downloadPdfReport("Sales — daily", salesExportSubtitle, headers, body);
            } else if (activeReportTab === "products") {
                const headers = ["Item", "Code", "Qty", "Total"];
                const body = productRows.map((r) => [r.itemName, r.itemCode, r.quantitySold, Number(r.totalSalesAmount).toFixed(2)]);
                await downloadPdfReport("Sales — product performance", salesExportSubtitle, headers, body);
            } else {
                const headers = ["Cashier", "Txns", "Total"];
                const body = cashierRows.map((r) => [r.cashierName ?? r.cashierUserId, r.saleCount, Number(r.totalSalesAmount).toFixed(2)]);
                await downloadPdfReport("Sales — cashier performance", salesExportSubtitle, headers, body);
            }
            message.success("PDF downloaded.");
        } catch {
            message.error("Could not generate PDF.");
        }
    };

    return (
        <div>
            <Title level={3}>Sales Reports</Title>

            <Card style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col span={10}>
                        <div>Date Range</div>
                        <RangePicker value={dateRange} onChange={(v: any) => v && setDateRange(v)} style={{ width: "100%" }} />
                    </Col>
                    <Col span={6}>
                        <div>Currency</div>
                        <Select
                            allowClear
                            placeholder="All currencies"
                            value={selectedCurrency}
                            onChange={setSelectedCurrency}
                            style={{ width: "100%" }}
                            options={currencyOptions}
                        />
                    </Col>
                    <Col span={8}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Statistic title="Total Sales" value={totalSales} precision={2} />
                            </Col>
                            <Col span={12}>
                                <Statistic title="Transactions" value={totalCount} />
                            </Col>
                        </Row>
                    </Col>
                    <Col span={24} style={{ marginTop: 12 }}>
                        <Button type="primary" icon={<DownloadOutlined />} style={{ marginRight: 8 }} onClick={handleSalesExportExcel}>
                            Export to Excel
                        </Button>
                        <Button icon={<DownloadOutlined />} onClick={() => void handleSalesExportPdf()}>
                            Export to PDF
                        </Button>
                    </Col>
                </Row>
            </Card>

            <Card title="Daily sales (aggregated)" style={{ marginBottom: 16 }}>
                {chartData.length === 0 ? (
                    <Empty
                        style={{ margin: "48px 0" }}
                        description="No daily sales for this date range (or currency filter). Adjust filters or pick a wider range."
                    />
                ) : (
                    <div style={{ width: "100%", height: 260, minWidth: 0 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                                <YAxis tick={{ fontSize: 11 }} width={48} />
                                <Tooltip
                                    cursor={false}
                                    formatter={(value: number) => [value.toFixed(2), "Sales"]}
                                />
                                <Bar dataKey="total" fill="#1890ff" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </Card>

            <Tabs
                activeKey={activeReportTab}
                onChange={setActiveReportTab}
                items={[
                    {
                        key: "daily",
                        label: "Daily Sales",
                        children: (
                            <Card>
                                <Table
                                    columns={dailyColumns}
                                    dataSource={dailyRows}
                                    loading={dailySalesLoading}
                                    rowKey={(r) => `${r.date}-${r.currencyCode}`}
                                    pagination={{ pageSize: 10 }}
                                />
                            </Card>
                        ),
                    },
                    {
                        key: "products",
                        label: "Product Performance",
                        children: (
                            <Card>
                                <Table
                                    columns={productColumns}
                                    dataSource={productRows}
                                    loading={productLoading}
                                    rowKey={(r) => r.inventoryItemId}
                                    pagination={{ pageSize: 10 }}
                                />
                            </Card>
                        ),
                    },
                    {
                        key: "cashiers",
                        label: "Cashier Performance",
                        children: (
                            <Card>
                                <Table
                                    columns={cashierColumns}
                                    dataSource={cashierRows}
                                    loading={cashierLoading}
                                    rowKey={(r) => r.cashierUserId}
                                    pagination={{ pageSize: 10 }}
                                />
                            </Card>
                        ),
                    },
                ]}
            />
        </div>
    );
};

